import { useMemo, useRef, useState, useLayoutEffect } from 'react'
import { Heart } from 'lucide-react'
import { UserAvatar, type UserAvatarSize } from './UserAvatar'
import { TotemAvatar } from './TotemAvatar'
import { CheckInIcon } from './CheckInIcon'
import { useFavoriteIdSet } from '../contexts/FavoriteFriendsContext'
import { favoriteRingClass } from '../lib/favoriteRing'
import type { UserProfile, TotemBorderStyle } from '../types'
import { cn } from '../lib/utils'
import { GoingCheckIcon } from './GoingCheckIcon'

export interface FacepileTotem {
  emoji: string
  imageUrl?: string
  borderStyle?: TotemBorderStyle
  partial?: boolean
  dimmed?: boolean
}

/**
 * Per-user overlay state: only part of the set, whether now is outside it (or
 * they checked in at another set), and whether they checked in here.
 */
export interface FacepileUserState {
  partial?: boolean
  dimmed?: boolean
  checkedIn?: boolean
}

const EMPTY_GOING: Set<string> = new Set()
const EMPTY_STATES: Record<string, FacepileUserState> = {}

// Facepile sizes are tighter than the rest of the app's avatars — its 'sm'
// is UserAvatar's 'xs' (24px) and its 'md' is UserAvatar's 'sm' (28px).
const AVATAR_SIZE: Record<'sm' | 'md', UserAvatarSize> = { sm: 'xs', md: 'sm' }
const AVATAR_PX: Record<'sm' | 'md', number> = { sm: 24, md: 28 }
const OVERLAP = 8
const FALLBACK_DIM: Record<'sm' | 'md', string> = {
  sm: 'h-6 w-6 text-[10px]',
  md: 'h-7 w-7 text-[11px]',
}

function maxAvatarsThatFit(width: number, total: number, propMax: number, avatarPx: number): number {
  if (width <= 0) return propMax
  const effectivePerExtra = avatarPx - OVERLAP
  for (let n = Math.min(propMax, total); n >= 1; n--) {
    const hasOverflow = total > n
    const w = avatarPx + (n - 1) * effectivePerExtra + (hasOverflow ? effectivePerExtra : 0)
    if (w <= width) return n
  }
  return 1
}

export function Facepile({
  userIds,
  goingUserIds = EMPTY_GOING,
  userStates = EMPTY_STATES,
  profiles,
  totems,
  max = 5,
  size = 'sm',
  responsive = false,
  animate = false,
}: {
  userIds: string[]
  goingUserIds?: Set<string>
  userStates?: Record<string, FacepileUserState>
  profiles: Record<string, UserProfile>
  totems?: FacepileTotem[]
  max?: number
  size?: 'sm' | 'md'
  responsive?: boolean
  animate?: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [fitMax, setFitMax] = useState(max)
  const avatarPx = AVATAR_PX[size]

  // Checked in beats going beats interested — someone confirmed at the stage is
  // who you actually want to find. Favorites float to the front *within* each
  // bucket, so they never outrank a stronger signal.
  const favoriteIdSet = useFavoriteIdSet()
  const orderedUserIds = useMemo(() => {
    const anyCheckedIn = userIds.some(u => userStates[u]?.checkedIn)
    if (favoriteIdSet.size === 0 && !anyCheckedIn) return userIds
    const rank = (u: string) => (userStates[u]?.checkedIn ? 0 : goingUserIds.has(u) ? 1 : 2)
    return [...userIds].sort(
      (a, b) => rank(a) - rank(b) || Number(favoriteIdSet.has(b)) - Number(favoriteIdSet.has(a)),
    )
  }, [userIds, favoriteIdSet, goingUserIds, userStates])

  const totemCount = totems?.length ?? 0
  const totalItems = orderedUserIds.length + totemCount

  useLayoutEffect(() => {
    if (!responsive) return
    const el = containerRef.current
    if (!el) return
    const measure = () => {
      const width = el.clientWidth
      setFitMax(maxAvatarsThatFit(width, totalItems, max, avatarPx))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [responsive, totalItems, max, avatarPx])

  const effectiveMax = responsive ? fitMax : max
  const userMax = Math.max(0, effectiveMax - totemCount)
  const shown = orderedUserIds.slice(0, userMax)
  const extra = orderedUserIds.length - shown.length
  const badgeDim = size === 'md' ? 'size-3.5' : 'size-3'
  // The interested heart is a touch smaller than the going check, so "going"
  // stays the louder signal in the stack.
  const heartDim = size === 'md' ? 'size-[13.2px]' : 'size-[11px]'
  const totemDim = FALLBACK_DIM[size]

  return (
    <div ref={containerRef} className={cn('flex items-center', animate && 'animate-facepile-in', responsive && 'min-w-0 p-0.5 -m-0.5')}>
      {totems?.map((t, i) => (
        <div
          key={`totem-${i}`}
          title={t.emoji}
          className="relative shrink-0"
          style={{ marginLeft: i > 0 ? '-8px' : 0, zIndex: (totemCount + shown.length + 1) - i }}
        >
          <TotemAvatar
            emoji={t.emoji}
            imageUrl={t.imageUrl}
            borderStyle={t.borderStyle}
            partial={t.partial}
            dimmed={t.dimmed}
            className={totemDim}
          />
        </div>
      ))}
      {shown.map((uid, i) => {
        const p = profiles[uid]
        const isGoing = goingUserIds.has(uid)
        const isFavorite = favoriteIdSet.has(uid)
        const state = userStates[uid]
        const baseTitle = p?.displayName ?? uid
        return (
          <div
            key={uid}
            title={state?.checkedIn ? `${baseTitle} (here)` : isGoing ? `${baseTitle} (going)` : `${baseTitle} (interested)`}
            className="relative shrink-0"
            style={{ marginLeft: (i > 0 || totemCount > 0) ? '-8px' : 0, zIndex: shown.length - i }}
          >
            <UserAvatar
              profile={p}
              fallbackName={uid}
              size={AVATAR_SIZE[size]}
              // Favorited friends get a golden ring so they stand out in the stack.
              className={cn(
                isFavorite ? favoriteRingClass : state?.checkedIn ? 'border-2 border-going' : 'border-2 border-background',
                state?.dimmed && 'grayscale opacity-50',
              )}
            />
            {/* Going gets a green check, interested a rose heart — the same
                language as the set cards. A range supersedes the check with a
                clock, still in Going green: a range only ever sits on a going
                mark, and blue read as "not going". Checked in outranks both. */}
            {state?.checkedIn ? (
              <CheckInIcon className={cn('absolute -bottom-0.5 -right-0.5', badgeDim)} strokeWidth={3} />
            ) : state?.partial ? (
              <GoingCheckIcon
                partial
                className={cn('absolute -bottom-0.5 -right-0.5', badgeDim)}
                strokeWidth={3}
              />
            ) : isGoing ? (
              <GoingCheckIcon
                className={cn('absolute -bottom-0.5 -right-0.5', badgeDim)}
                strokeWidth={3}
              />
            ) : (
              <Heart
                className={cn('absolute -bottom-0.5 -right-0.5 fill-interested text-interested', heartDim)}
                strokeWidth={1}
              />
            )}
          </div>
        )
      })}
      {extra > 0 && (
        <div
          className={cn(
            'rounded-full border-2 border-background bg-muted flex items-center justify-center font-bold shrink-0',
            FALLBACK_DIM[size],
          )}
          style={{ marginLeft: '-8px' }}
        >
          +{extra}
        </div>
      )}
    </div>
  )
}
